<script setup lang="ts">
import { reactive, ref, computed } from "vue";

import {
  type BriefSection,
  type ProjectDetail,
  type WorkPackConstructionBrief,
  type WorkPackRow,
  briefFromDocument,
  improveConstructionDescription,
  structureConstructionBrief,
  suggestConstructionBrief,
  updateConstructionBrief,
} from "@/api/client";

// Minimal typing for the browser SpeechRecognition API (not in lib.dom).
interface SpeechResultLike { 0: { transcript: string }; isFinal: boolean }
interface SpeechEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechResultLike>;
}
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: SpeechEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type SpeechCtor = new () => SpeechRecognitionLike;

const props = defineProps<{
  pack: WorkPackRow;
  project?: ProjectDetail;
  priorPacks?: WorkPackRow[];
}>();
const emit = defineEmits<{ saved: [] }>();

type FieldKey = keyof WorkPackConstructionBrief;

const SECTIONS: Array<{
  key: BriefSection;
  title: string;
  fields: Array<{ key: FieldKey; label: string; rows: number }>;
}> = [
  {
    key: "overview",
    title: "1 · Overview & objectives",
    fields: [
      { key: "strategic_purpose", label: "Strategic purpose — the “why”", rows: 2 },
      { key: "success_criteria", label: "Success criteria (time / cost / quality)", rows: 2 },
    ],
  },
  {
    key: "scope",
    title: "2 · Scope & boundaries",
    fields: [
      { key: "included_works", label: "Included works", rows: 3 },
      { key: "exclusions", label: "Exclusions (what this does NOT cover)", rows: 2 },
      { key: "site_information", label: "Site information — access, services, conditions", rows: 2 },
    ],
  },
  {
    key: "requirements",
    title: "3 · Requirements & constraints",
    fields: [
      { key: "functional_requirements", label: "Functional requirements", rows: 2 },
      { key: "technical_specs", label: "Technical & performance specs", rows: 2 },
      { key: "budget", label: "Budget", rows: 1 },
      { key: "milestones", label: "Milestones — start / end / handover", rows: 2 },
    ],
  },
  {
    key: "risks",
    title: "4 · Risks & governance",
    fields: [
      { key: "key_risks", label: "Key risks (commercial / programme)", rows: 2 },
      { key: "assumptions", label: "Assumptions", rows: 2 },
      { key: "procurement_strategy", label: "Procurement strategy", rows: 1 },
      { key: "change_control", label: "Change control", rows: 1 },
    ],
  },
];

const brief = reactive<Record<string, string>>({});
for (const section of SECTIONS) {
  for (const f of section.fields) {
    brief[f.key] = props.pack.construction_brief?.[f.key] ?? "";
  }
}

const busy = ref(false);
const message = ref<string | null>(null);
// AI actions (Structure / Read document) report inline in the "Describe the job"
// panel, next to their buttons — a top-of-form message reads as "nothing happened".
const aiMessage = ref<string | null>(null);

// Unsaved-changes tracking, so the user gets a clear "needs saving" / "saved" cue.
function cleanBrief(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(brief)) {
    if (v && v.trim()) out[k] = v.trim();
  }
  return out;
}
const savedSnapshot = ref(JSON.stringify(cleanBrief()));
const savedOnce = ref(!!props.pack.construction_brief);
const dirty = computed(
  () => JSON.stringify(cleanBrief()) !== savedSnapshot.value,
);

// The most recent OTHER pack on this project that has a construction brief.
const sourcePack = computed(() =>
  (props.priorPacks ?? [])
    .filter((p) => p.id !== props.pack.id && p.construction_brief)
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))[0],
);

/** Fill only fields the user hasn't typed into (never clobber their work). */
function fillEmpty(values: Partial<Record<FieldKey, string | undefined>>): number {
  let filled = 0;
  for (const [key, value] of Object.entries(values)) {
    if (value && !brief[key]?.trim()) {
      brief[key] = value;
      filled += 1;
    }
  }
  return filled;
}

function copyFromPrevious(): void {
  const src = sourcePack.value?.construction_brief;
  if (!src) return;
  const n = fillEmpty(src);
  message.value = n
    ? `Copied ${n} field(s) from “${sourcePack.value?.title}”. Edit, then save.`
    : "Nothing to copy — your fields are already filled.";
}

// ---- Describe the job → AI structures it into the brief (Phase 3) ----
const transcript = ref("");
const structuring = ref(false);
const improving = ref(false);

/** Apply an AI section draft: fill empty fields, and offer to overwrite any the
 *  user has already filled — so "Draft sections below" never feels like a no-op
 *  when the sections are pre-filled (from a template / previous pack). */
function applyDraft(
  values: Partial<Record<FieldKey, string | undefined>>,
  model: string | null,
): string {
  const suffix = model ? ` · ${model}` : "";
  const entries = Object.entries(values).filter(
    ([, v]) => v && v.trim(),
  ) as Array<[string, string]>;
  if (!entries.length) {
    return "AI couldn't map your description to any of the sections below.";
  }
  const empties = entries.filter(([k]) => !brief[k]?.trim());
  const filled = entries.filter(([k]) => brief[k]?.trim());
  for (const [k, v] of empties) brief[k] = v;

  let overwrote = 0;
  if (
    filled.length &&
    window.confirm(
      `The AI also has content for ${filled.length} section field(s) you've ` +
        "already filled. Overwrite them with the AI draft?",
    )
  ) {
    for (const [k, v] of filled) {
      brief[k] = v;
      overwrote += 1;
    }
  }
  const parts: string[] = [];
  if (empties.length) parts.push(`filled ${empties.length}`);
  if (overwrote) parts.push(`replaced ${overwrote}`);
  return parts.length
    ? `AI ${parts.join(" and ")} section field(s)${suffix}. Review, edit, then save.`
    : "Left your existing sections unchanged.";
}

/** Rewrite the "Describe the job" prose into clearer text (the box itself). */
async function improveDescription(): Promise<void> {
  if (!transcript.value.trim()) return;
  recogniser?.stop();
  improving.value = true;
  aiMessage.value = null;
  try {
    const res = await improveConstructionDescription(
      props.pack.id,
      transcript.value.trim(),
    );
    transcript.value = res.text;
    aiMessage.value = `Description tidied up${res.model ? ` · ${res.model}` : ""}. Review it above, then draft the sections.`;
  } catch (e) {
    aiMessage.value = e instanceof Error ? e.message : String(e);
  } finally {
    improving.value = false;
  }
}

const speechCtor = ((): SpeechCtor | undefined => {
  const w = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
})();
const speechSupported = !!speechCtor;
const dictating = ref(false);
let recogniser: SpeechRecognitionLike | null = null;

function toggleDictation(): void {
  if (dictating.value) {
    recogniser?.stop();
    return;
  }
  if (!speechCtor) return;
  recogniser = new speechCtor();
  recogniser.lang = "en-GB";
  recogniser.interimResults = false;
  recogniser.continuous = true;
  recogniser.onresult = (e) => {
    for (let i = e.resultIndex; i < e.results.length; i += 1) {
      const r = e.results[i];
      if (r.isFinal) {
        transcript.value = `${transcript.value} ${r[0].transcript}`.trim();
      }
    }
  };
  recogniser.onend = () => (dictating.value = false);
  dictating.value = true;
  recogniser.start();
}

async function structureFromTranscript(): Promise<void> {
  if (!transcript.value.trim()) return;
  recogniser?.stop();
  structuring.value = true;
  aiMessage.value = null;
  try {
    const res = await structureConstructionBrief(props.pack.id, transcript.value.trim());
    aiMessage.value = applyDraft(
      res.suggestions as Partial<Record<FieldKey, string>>,
      res.model,
    );
  } catch (e) {
    aiMessage.value = e instanceof Error ? e.message : String(e);
  } finally {
    structuring.value = false;
  }
}

// Draft from an attached document (text PDF / text). Only docs with a file_id.
const documents = computed(() =>
  (props.pack.documents ?? []).filter((d) => d.file_id),
);
const selectedDocId = ref("");
const reading = ref(false);
async function draftFromDocument(): Promise<void> {
  if (!selectedDocId.value) return;
  reading.value = true;
  aiMessage.value = null;
  try {
    const res = await briefFromDocument(props.pack.id, selectedDocId.value);
    const n = fillEmpty(res.suggestions as Partial<Record<FieldKey, string>>);
    aiMessage.value = n
      ? `AI filled ${n} field(s) from the document${res.model ? ` · ${res.model}` : ""}. Review, edit, then save.`
      : "AI mapped this to fields you've already filled — clear a field to let it draft there.";
  } catch (e) {
    aiMessage.value = e instanceof Error ? e.message : String(e);
  } finally {
    reading.value = false;
  }
}

// Let the parent (document tiles) trigger a draft from a specific document.
function draftFrom(documentId: string): Promise<void> {
  selectedDocId.value = documentId;
  return draftFromDocument();
}
defineExpose({ draftFrom });

// ✨ AI draft for one section — fills empty fields only; user reviews + saves.
const suggesting = ref<string | null>(null);
async function suggest(section: BriefSection): Promise<void> {
  suggesting.value = section;
  message.value = null;
  try {
    const res = await suggestConstructionBrief(props.pack.id, section);
    const n = fillEmpty(res.suggestions as Partial<Record<FieldKey, string>>);
    message.value = n
      ? `AI drafted ${n} field(s)${res.model ? ` · ${res.model}` : ""}. Review, edit, then save.`
      : "AI had nothing to add — those fields are already filled.";
  } catch (e) {
    message.value = e instanceof Error ? e.message : String(e);
  } finally {
    suggesting.value = null;
  }
}

function prefillFromProject(): void {
  const p = props.project;
  if (!p) return;
  const site = [p.location_name, p.postcode].filter(Boolean).join(", ");
  const n = fillEmpty({
    site_information: site || undefined,
    strategic_purpose:
      p.client_name || p.service_area
        ? `${p.service_area ? p.service_area + " works" : "Works"} for ${p.client_name ?? "the client"} on ${p.name}.`
        : undefined,
  });
  message.value = n
    ? `Pre-filled ${n} field(s) from the project. Edit, then save.`
    : "Nothing to pre-fill — those fields are already filled.";
}

async function save(): Promise<void> {
  busy.value = true;
  message.value = null;
  try {
    // Drop empty strings so we store a clean object.
    const payload: WorkPackConstructionBrief = {};
    for (const [k, v] of Object.entries(brief)) {
      if (v && v.trim()) (payload as Record<string, string>)[k] = v.trim();
    }
    await updateConstructionBrief(props.pack.id, payload);
    savedSnapshot.value = JSON.stringify(payload);
    savedOnce.value = true;
    message.value = "Construction brief saved.";
    emit("saved");
  } catch (e) {
    message.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="grid gap-5">
    <div class="flex flex-wrap items-center gap-2">
      <button
        v-if="sourcePack"
        type="button"
        class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="copyFromPrevious"
      >
        Copy from previous pack
      </button>
      <button
        v-if="project"
        type="button"
        class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="prefillFromProject"
      >
        Pre-fill from project
      </button>
      <span class="grow" />
      <span
        v-if="dirty"
        class="text-xs font-semibold text-semantic-warning"
      >● Unsaved changes</span>
      <span
        v-else-if="savedOnce"
        class="text-xs font-semibold text-semantic-success"
      >✓ Saved</span>
      <button
        type="button"
        class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
        :disabled="busy || !dirty"
        @click="save"
      >
        {{ busy ? "Saving…" : dirty ? "Save brief" : "Saved" }}
      </button>
    </div>
    <p
      v-if="message"
      class="text-sm text-neutral-500"
    >
      {{ message }}
    </p>

    <!-- Describe the job → AI structures it into the sections below -->
    <section class="rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-5">
      <h3 class="font-semibold text-neutral-900">
        Describe the job
      </h3>
      <p class="mt-0.5 text-xs text-neutral-500">
        Dictate or type a plain-English description of the works.
        <strong>Improve description</strong> rewrites the text above into
        clearer prose; <strong>Draft sections below</strong> maps it into the
        structured brief sections (review &amp; edit before saving).
      </p>
      <textarea
        v-model="transcript"
        rows="3"
        placeholder="e.g. We're replacing OLE mast 47 on the up main next to the live OLE; needs an isolation; two-night possession…"
        class="mt-3 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
      />
      <div class="mt-2 flex flex-wrap items-center gap-2">
        <button
          v-if="speechSupported"
          type="button"
          class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          :class="dictating ? 'border-semantic-danger text-semantic-danger' : ''"
          @click="toggleDictation"
        >
          {{ dictating ? "■ Stop dictation" : "🎤 Dictate" }}
        </button>
        <button
          type="button"
          class="rounded-md border border-brand-primary/40 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/5 disabled:opacity-50"
          :disabled="improving || !transcript.trim()"
          @click="improveDescription"
        >
          {{ improving ? "Improving…" : "✨ Improve description" }}
        </button>
        <button
          type="button"
          class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
          :disabled="structuring || !transcript.trim()"
          @click="structureFromTranscript"
        >
          {{ structuring ? "Drafting…" : "✨ Draft sections below" }}
        </button>
      </div>
      <p
        v-if="aiMessage"
        class="mt-2 text-sm font-medium text-brand-primary"
      >
        {{ aiMessage }}
      </p>

      <!-- …or draft from an attached document (RAMS / spec, text-based PDF) -->
      <div
        v-if="documents.length"
        class="mt-3 flex flex-wrap items-center gap-2 border-t border-brand-primary/15 pt-3"
      >
        <span class="text-xs text-neutral-500">…or from a document:</span>
        <select
          v-model="selectedDocId"
          class="rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
        >
          <option value="">
            Choose a document…
          </option>
          <option
            v-for="d in documents"
            :key="d.document_id"
            :value="d.document_id"
          >
            {{ d.title }}
          </option>
        </select>
        <button
          type="button"
          class="rounded-md border border-brand-primary/40 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/5 disabled:opacity-50"
          :disabled="reading || !selectedDocId"
          @click="draftFromDocument"
        >
          {{ reading ? "Reading…" : "✨ Read document → draft" }}
        </button>
      </div>
    </section>

    <section
      v-for="section in SECTIONS"
      :key="section.title"
      class="rounded-xl border border-neutral-200 bg-neutral-0 p-5"
    >
      <div class="mb-3 flex items-center justify-between gap-2">
        <h3 class="font-semibold text-neutral-900">
          {{ section.title }}
        </h3>
        <button
          type="button"
          class="shrink-0 rounded-md border border-brand-primary/40 px-2.5 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary/5 disabled:opacity-50"
          :disabled="suggesting === section.key"
          @click="suggest(section.key)"
        >
          {{ suggesting === section.key ? "Drafting…" : "✨ Suggest" }}
        </button>
      </div>
      <div class="grid gap-3">
        <label
          v-for="f in section.fields"
          :key="f.key"
          class="grid gap-1"
        >
          <span class="text-sm text-neutral-600">{{ f.label }}</span>
          <textarea
            v-model="brief[f.key]"
            :rows="f.rows"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </label>
      </div>
    </section>
  </div>
</template>
